import React, { useState } from 'react';
import { ModalProps } from '@modals/types';
import Overlay from '@components/Overlay';
import { Button, Card, Divider, Icon, Intent } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import { TaskPublishType, TaskQueue, TaskState } from 'dy-frontend-http-repository/lib/data/enums';
import DevText from '@components/Text';
import { IconName } from '@blueprintjs/icons';
import { Endpoints, taskStateInformation } from '@data/consts';
import RouterLink from '@components/RouterLink';
import { taskApi } from '@pages/Task/store/apis';
import { resumeTask } from '@pages/Task/store/effects';

export interface ResumeTaskModalProps {
    taskId: ID;
    ownerUserId: ID;
    taskPublishType: TaskPublishType | null;
    currentTaskState: TaskState;
    currentTaskQueue: TaskQueue;
}

type Props = ModalProps<ResumeTaskModalProps>;

const ResumeTaskModal: React.FC<Props> = ({
    closeModal,
    data,
}) => {
    // Task restoring
    const [isTaskResuming, setIsTaskResuming] = useState(false);
    const [isTaskResumeDone, setIsTaskResumeDone] = useState(false);

    // O_o?
    if (!data) {
        closeModal?.();
        return null;
    }

    // Check: is task published
    const isDraft = data.currentTaskQueue === TaskQueue.DRAFT || data.taskPublishType === null;

    // Check: is delivered
    const isDelivered = data.currentTaskState === TaskState.DELIVERED;

    // Get readable state name
    const readableStateName = taskStateInformation[data.currentTaskState]?.title ?? 'Unknown';

    // Change: will be moved to the backlog?
    const isBacklogMovementChange = (
        // If not draft or delivered...
        !isDraft && !isDelivered

        // ... and not in backlog state already
        && data.currentTaskQueue !== TaskQueue.BACKLOG
    );

    // Change: will be locked in backlog
    const isBacklogAutomationLockChange = (
        // If not draft or delivered...
        !isDraft && !isDelivered

        // ... and published through `quota`
        && data.taskPublishType === TaskPublishType.QUOTA
    );

    const handleModalClose = () => {
        if (isTaskResuming) {
            return;
        }

        // O_o?
        closeModal?.();
        return null;
    };

    const handleResumeFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setIsTaskResuming(true);

        resumeTask(data.taskId)
            .then(() => {
                setIsTaskResumeDone(true);

                taskApi.resume();
            })
            .catch((e) => console.error)
            .finally(() => setIsTaskResuming(false));

        return null;
    };

    const renderExpectedChangeItem = (icon: IconName, title: String, description: String) => {
        return (
            <div>
                <Flex align="center" className="mb-1">
                    <Icon icon={icon} className="mr-1" />
                    <Heading type="h5">{title}</Heading>
                </Flex>
                <DevText muted running>
                    {description}
                </DevText>
            </div>
        );
    };

    const renderExpectedChangesInfo = () => {
        const changes: React.ReactNode[] = [];

        // Default change: restore to <state> state
        changes.push(renderExpectedChangeItem(
            'history',
            `Resume request back to "${readableStateName}" state`,
            `Request was in "${readableStateName}" state before it was paused`,
        ));

        // Backlog movement
        if (isBacklogMovementChange) {
            changes.push(renderExpectedChangeItem(
                'unarchive',
                `Move request to backlog`,
                `
                    To prevent confusion with request order, this request will be moved to backlog.
                    After that, you will be able to manually move it to "active" requests on the client's
                    "Requests" page.
                `,
            ));
        }

        // Backlog queue lock
        if (isBacklogAutomationLockChange) {
            changes.push(renderExpectedChangeItem(
                'lock',
                `Lock request in the backlog`,
                `
                    Client used their subscription plan limits when publishing this request. Such requests are moved
                    from backlog to active automatically. To prevent this potentially unwanted behaviour, we will
                    lock any automatic movement on this request. After restoring request, you will be able to remove
                    this lock on the client's "Requests" page.
                `,
            ));
        }

        return changes.map((item, index) => (
            <div key={`resume-change-${index}`} className="mb-2">{item}</div>
        ));
    };

    const renderResumeForm = () => {
        return (
            <form onSubmit={handleResumeFormSubmit}>
                {renderExpectedChangesInfo()}

                <Divider className="mb-2" />

                <Flex align="center" justify="space-between">
                    <DevText muted>Proceed with resuming the request?</DevText>
                    <div>
                        <Button minimal onClick={closeModal} className="mr-1" disabled={isTaskResuming}>
                            No, cancel
                        </Button>
                        <Button
                            intent={Intent.PRIMARY}
                            loading={isTaskResuming}
                            type="submit"
                        >
                            Yes, resume request
                        </Button>
                    </div>
                </Flex>
            </form>
        );
    };

    const renderResumeSuccess = () => {
        return (
            <div>
                <Flex align="center" className="mb-2">
                    <Icon icon="tick" size={35} intent={Intent.SUCCESS} className="mr-1" />
                    <Heading type="h5">Request successfully resumed</Heading>
                </Flex>

                <DevText muted className="mb-2">This window can now be closed</DevText>

                {(isBacklogMovementChange || isBacklogAutomationLockChange) && (
                    <DevText>
                        You can move request from backlog, or unlock it on{' '}
                        <RouterLink
                            className="mr-1"
                            onClick={closeModal}
                            to={Endpoints.CLIENT_TASKS.replace(':clientId', data.ownerUserId)}
                        >
                            client's "Requests" page
                        </RouterLink>
                    </DevText>
                )}
            </div>
        );
    };

    const renderModalTitleCaption = () => {
        // Initial
        if (!isTaskResuming && !isTaskResumeDone) {
            return (
                <DevText muted>Here is what will happen to request after it is resumed</DevText>
            );
        }

        // Restoring...
        if (isTaskResuming) {
            return (
                <DevText muted>Resuming request...</DevText>
            );
        }

        // Restore done
        if (!isTaskResuming && isTaskResumeDone) {
            return (
                <DevText muted>Request successfully resumed!</DevText>
            );
        }

        return null;
    };

    return (
        <Overlay isOpen onClose={handleModalClose}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="start" justify="space-between">
                    <div>
                        <Heading type="h4" className="mb-1">Resume paused request</Heading>
                        {renderModalTitleCaption()}
                    </div>
                    <Button minimal icon="cross" onClick={closeModal} disabled={isTaskResuming} />
                </Flex>

                <Divider className="mb-2" />

                {!isTaskResumeDone ? renderResumeForm() : renderResumeSuccess()}
            </Card>
        </Overlay>
    );
};

export default ResumeTaskModal;
